import { Box, Button, Typography, IconButton, Stack } from '@mui/material';
import { CaretLeft, CaretRight, PencilSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Api } from '../api';
import AppointmentPopup from './appointments/AppointmentPopup';
import AvailableBlockPopup from './appointments/AvailableBlockPopup';
import CalendarHeader from './appointments/CalendarHeader';
import TimeColumn from './appointments/TimeColumn';
import DayColumn from './appointments/DayColumn';
import RescheduleAppointmentModal from './appointments/RescheduleAppointmentModal';

interface AppointmentsCalendarProps {
  title?: string;
  showEditButton?: boolean;
  editButtonText?: string;
  onEditClick?: () => void;
  clinicianId: string;
  timezone: string;
  days: Array<{ name: string; key: string; date: string }>;
  hours: string[];
  availableHours: { [date: string]: { start: string; end: string }[] };
  appointments: Api.Appointment.V_ClinicianSub[];
  bufferMinutes: number;
  onRescheduled?: () => void;
  onPreviousWeek?: () => void;
  onNextWeek?: () => void;
  onToday?: () => void;
  onMarkUnavailable?: (date: string, timeSlot: string) => void;
}

export default function AppointmentsCalendar({
  title = '',
  showEditButton = false,
  editButtonText = 'Edit your schedule',
  onEditClick,
  clinicianId,
  timezone,
  days,
  hours,
  availableHours,
  appointments,
  bufferMinutes,
  onRescheduled,
  onPreviousWeek,
  onNextWeek,
  onToday,
  onMarkUnavailable,
}: AppointmentsCalendarProps) {
  const navigate = useNavigate();
  const [popupAnchorEl, setPopupAnchorEl] = useState<HTMLElement | null>(null);

  const [selectedAppointment, setSelectedAppointment] =
    useState<Api.Appointment.V_ClinicianSub | null>(null);

  const [availablePopupAnchorEl, setAvailablePopupAnchorEl] =
    useState<HTMLElement | null>(null);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] =
    useState<Api.Appointment.V_ClinicianSub | null>(null);

  const handleAppointmentClick = (
    event: React.MouseEvent<HTMLElement>,
    appointment: Api.Appointment.V_ClinicianSub,
  ) => {
    setPopupAnchorEl(event.currentTarget);
    setSelectedAppointment(appointment);
  };

  const handleAvailableSlotClick = (
    event: React.MouseEvent<HTMLElement>,
    timeSlot: string,
    date: string,
  ) => {
    setAvailablePopupAnchorEl(event.currentTarget);
    setSelectedTimeSlot(timeSlot);
    setSelectedDate(date);
  };

  const handlePopupClose = () => {
    setPopupAnchorEl(null);
    setSelectedAppointment(null);
  };

  const handleOpenReschedule = (apt: Api.Appointment.V_ClinicianSub) => {
    setRescheduleAppt(apt);
    setRescheduleOpen(true);
    handlePopupClose();
  };

  const handleAvailablePopupClose = () => {
    setAvailablePopupAnchorEl(null);
    setSelectedTimeSlot('');
    setSelectedDate('');
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{title}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {showEditButton && (
            <Button
              startIcon={<PencilSimple />}
              variant="contained"
              onClick={
                onEditClick || (() => navigate('/appointments/edit-schedule'))
              }
            >
              {editButtonText}
            </Button>
          )}
          <IconButton onClick={onPreviousWeek}>
            <CaretLeft color="#667085" />
          </IconButton>
          <Button variant="outlined" onClick={onToday}>
            Today
          </Button>
          <IconButton onClick={onNextWeek}>
            <CaretRight color="#667085" />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: 800 }}>
          <CalendarHeader days={days} />
          <Box sx={{ position: 'relative', display: 'flex' }}>
            <TimeColumn hours={hours} />
            {days.map((day, dayIndex) => (
              <DayColumn
                key={day.key}
                day={day}
                dayIndex={dayIndex}
                timezone={timezone}
                onAppointmentClick={handleAppointmentClick}
                onAvailableSlotClick={handleAvailableSlotClick}
                availableHours={availableHours}
                appointments={appointments}
                days={days}
                hours={hours}
                bufferMinutes={bufferMinutes}
              />
            ))}
          </Box>
        </Box>
      </Box>

      <AppointmentPopup
        appointment={selectedAppointment}
        anchorEl={popupAnchorEl}
        open={Boolean(popupAnchorEl)}
        onClose={handlePopupClose}
        onReschedule={handleOpenReschedule}
        timezone={timezone}
      />

      <AvailableBlockPopup
        anchorEl={availablePopupAnchorEl}
        open={Boolean(availablePopupAnchorEl)}
        onClose={handleAvailablePopupClose}
        timeSlot={selectedTimeSlot}
        date={selectedDate}
        clinicianId={clinicianId}
        onMarkUnavailable={onMarkUnavailable}
      />

      {rescheduleAppt && (
        <RescheduleAppointmentModal
          open={rescheduleOpen}
          onClose={() => setRescheduleOpen(false)}
          appointment={rescheduleAppt}
          clinicianId={clinicianId}
          onSuccess={onRescheduled}
        />
      )}
    </>
  );
}
